import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router';
import { toast } from 'react-toastify';
import { itemsAPI } from '../../api/itemsAPI';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SEO from 'components/seo/SEO';
import styled from 'styled-components';
import { getMarketplaceItemDetailPath, getEditListingPath } from '../../utils/itemLinks';
import { formatListingPriceLabel } from '../../utils/formatPrice';
import { collectListingImageUrls } from '../../utils/listingImages';

const ItemsContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem 1rem;
  min-height: 100vh;
  background: #a8e6cf;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
`;

const CatalogueNotice = styled.p`
  margin: -1rem 0 1.5rem;
  padding: 0.875rem 1rem;
  border-radius: 0.5rem;
  background-color: #f0f4ff;
  border: 1px solid #c7d2fe;
  color: var(--text-primary);
  font-size: 0.95rem;
  line-height: 1.5;
`;

const CreateItemButton = styled(Link)`
  background-color: var(--primary-color);
  color: white;
  padding: 0.875rem 1.5rem;
  border-radius: 0.625rem;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(102, 126, 234, 0.4);
  }
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 1rem;
  }
`;

const ItemCard = styled.div`
  background-color: var(--bg-primary);
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;
  color: inherit;
  display: flex;
  flex-direction: column;
  position: relative;
  opacity: ${props => (props.$soldOut ? 0.65 : 1)};
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
  }
  ${props => props.$soldOut && `
    &:hover { transform: none; }
  `}
`;

const ItemImage = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    transform: scale(1.1);
  }
`;

const SoldOutOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0;
`;

const SoldOutLabel = styled.span`
  background: rgba(0,0,0,0.75);
  color: #fff;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-weight: 700;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ItemInfo = styled.div`
  padding: 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ItemTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ItemPrice = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-top: auto;
`;

const ItemMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const StockBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => (props.$inStock > 0 ? '#10b981' : '#6b7280')};
  color: white;
`;

const VisibilityBadge = styled.span`
  padding: 0.2rem 0.6rem;
  border-radius: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
  background-color: ${props => (props.$private ? '#f59e0b' : '#3b82f6')};
  color: white;
`;

const ItemBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
`;

const EmptyText = styled.p`
  font-size: 1rem;
  margin-bottom: 2rem;
`;

const EditButton = styled(Link)`
  background-color: var(--primary-color);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.875rem;
  display: inline-block;
  text-align: center;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-1px);
  }
`;

const DeleteButton = styled.button`
  background-color: #dc2626;
  color: #fff;
  border: none;
  padding: 0.55rem 1rem;
  border-radius: 0.375rem;
  font-weight: 700;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #b91c1c;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;

  > * {
    flex: 1;
    min-width: 5.5rem;
    text-align: center;
  }
`;

const MyItemsPage = () => {
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchMyItems = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const allItems = await itemsAPI.getItemsByUser(user.id);
      const list = Array.isArray(allItems) ? allItems : [];

      const sortedItems = [...list].sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateB - dateA;
      });

      setItems(sortedItems);
    } catch (err) {
      console.error('Error fetching my items:', err);
      setError('Failed to load your items. Please try again.');
      toast.error('Failed to load your items');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchMyItems();
  }, [fetchMyItems, location.key]);

  const handleDelete = async (item) => {
    const confirmed = window.confirm(
      `Delete "${item.title}"? It will be removed from your catalogue and the storefront.`
    );
    if (!confirmed) return;

    try {
      setDeletingId(item.id);
      await itemsAPI.deleteItem(item.id);
      setItems((prev) => prev.filter((row) => row.id !== item.id));
      toast.success('Item deleted');
    } catch (err) {
      const message =
        err?.response?.data?.error || 'Failed to delete item. Please try again.';
      toast.error(typeof message === 'string' ? message : 'Failed to delete item');
      fetchMyItems();
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <ItemsContainer>
        <LoadingSpinner />
      </ItemsContainer>
    );
  }

  if (error) {
    return (
      <ItemsContainer>
        <EmptyState>
          <EmptyIcon>⚠️</EmptyIcon>
          <EmptyTitle>Error</EmptyTitle>
          <EmptyText>{error}</EmptyText>
        </EmptyState>
      </ItemsContainer>
    );
  }

  return (
    <>
      <SEO title="My Catalogue - FLEA" description="View and manage your listed items" />
      <ItemsContainer>
        <PageHeader>
          <PageTitle>My Catalogue</PageTitle>
          <CreateItemButton to="/create-item">+ Offer New Item</CreateItemButton>
        </PageHeader>

        <CatalogueNotice>
          You can see all the items you&apos;ve posted here, including anonymous ones — but anonymous
          items are still anonymous to other people and don&apos;t show on your public catalogue.
          {' '}
          Use the red <strong>Delete</strong> button on a card to remove a listing, or open{' '}
          <strong>Edit</strong> and scroll to <strong>Delete listing</strong> at the bottom.
        </CatalogueNotice>

        {items.length === 0 ? (
          <EmptyState>
            <EmptyIcon>📦</EmptyIcon>
            <EmptyTitle>No Items In Your Catalogue</EmptyTitle>
            <EmptyText>
              You haven't listed any items yet. Start selling by offering your first item!
            </EmptyText>
            <CreateItemButton to="/create-item">+ Offer Your First Item</CreateItemButton>
          </EmptyState>
        ) : (
          <ItemsGrid>
            {items.map((item) => {
              const soldOut = !item.in_stock || item.in_stock === 0;
              const detailPath = getMarketplaceItemDetailPath(item);
              const cardImage = collectListingImageUrls(item)[0];
              return (
                <ItemCard key={item.id} $soldOut={soldOut}>
                  <ItemImage as={Link} to={detailPath} state={{ itemId: item.id }}>
                    {cardImage ? (
                      <img src={cardImage} alt={item.title} />
                    ) : (
                      <div style={{ fontSize: '3rem' }}>📦</div>
                    )}
                    {soldOut && (
                      <SoldOutOverlay>
                        <SoldOutLabel>Sold out</SoldOutLabel>
                      </SoldOutOverlay>
                    )}
                  </ItemImage>
                  <ItemInfo>
                    <ItemTitle as={Link} to={detailPath} state={{ itemId: item.id }}>{item.title}</ItemTitle>
                    <ItemMeta>
                      <ItemPrice>{formatListingPriceLabel(item)}</ItemPrice>
                      <StockBadge $inStock={item.in_stock ?? 0}>
                        {soldOut ? 'Sold out' : `In Stock: ${item.in_stock}`}
                      </StockBadge>
                    </ItemMeta>
                    <ItemBadges>
                      <VisibilityBadge $private={!!item.is_anonymous}>
                        {item.is_anonymous ? 'Private listing' : 'Public listing'}
                      </VisibilityBadge>
                    </ItemBadges>
                    <CardActions>
                      <EditButton to={getEditListingPath(item)}>Edit</EditButton>
                      <DeleteButton
                        type="button"
                        disabled={deletingId === item.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDelete(item);
                        }}
                      >
                        {deletingId === item.id ? 'Deleting…' : 'Delete listing'}
                      </DeleteButton>
                    </CardActions>
                  </ItemInfo>
                </ItemCard>
              );
            })}
          </ItemsGrid>
        )}
      </ItemsContainer>
    </>
  );
};

export default MyItemsPage;
